import { useState } from 'react';
import { adminApi } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Loading, ApiError, EmptyState } from './Loading';
import { Modal } from './Modal';
import { Badge } from './Badge';
import { formatPrice, formatDate } from '../../utils/helpers';

const TYPE_STYLE = {
  laverie_revenue: {
    label: 'Revenu laverie',
    cls: 'badge-green',
    sign: '+',
    color: 'var(--green)',
  },
  commission_earned: {
    label: 'Commission',
    cls: 'badge-green',
    sign: '+',
    color: 'var(--green)',
  },
  cash_collected: {
    label: 'Espèces collectées',
    cls: 'badge-blue',
    sign: '+',
    color: 'var(--blue)',
  },
  settlement_paid: {
    label: 'Règlement payé',
    cls: 'badge-orange',
    sign: '-',
    color: 'var(--orange)',
  },
  service_revenue: {
    label: 'Revenu service',
    cls: 'badge-green',
    sign: '+',
    color: 'var(--green)',
  },
  settlement_received: {
    label: 'Règlement reçu',
    cls: 'badge-green',
    sign: '+',
    color: 'var(--green)',
  },
  invoice_paid: {
    label: 'Facture réglée',
    cls: 'badge-green',
    sign: '+',
    color: 'var(--green)',
  },
  debit: {
    label: 'Débit',
    cls: 'badge-red',
    sign: '-',
    color: 'var(--red)',
  },
  credit: {
    label: 'Crédit',
    cls: 'badge-green',
    sign: '+',
    color: 'var(--green)',
  },
};

const CartableContent = ({ userId }) => {
  const [page] = useState(1);

  const { data: raw, loading, error, refetch } = useApi(
    () => adminApi.getUserLedger(userId, { page, limit: 20 }),
    [userId, page]
  );

  const entries = raw?.data?.transactions ?? [];
  const summary = raw?.data?.summary ?? {};

  const balance = summary.net ?? 0;
  const totalCredit = summary.total_credit ?? 0;
  const totalDebit = summary.total_debit ?? 0;

  if (loading) return <Loading text="Chargement du cartable..." />;
  if (error) return <ApiError message={error} onRetry={refetch} />;

  return (
    <div>
      {/* Résumé */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3,1fr)',
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            background: 'var(--bg3)',
            borderRadius: 12,
            padding: '14px',
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: 'var(--text2)',
              marginBottom: 4,
            }}
          >
            SOLDE
          </div>

          <div
            style={{
              fontFamily: 'Syne',
              fontWeight: 800,
              fontSize: 22,
              color: balance >= 0 ? 'var(--green)' : 'var(--red)',
            }}
          >
            {balance >= 0 ? '+' : ''}
            {formatPrice(balance)}
          </div>
        </div>

        <div
          style={{
            background: 'var(--bg3)',
            borderRadius: 12,
            padding: '14px',
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: 'var(--text2)',
              marginBottom: 4,
            }}
          >
            CRÉDITS
          </div>

          <div
            style={{
              fontFamily: 'Syne',
              fontWeight: 800,
              fontSize: 20,
              color: 'var(--green)',
            }}
          >
            +{formatPrice(totalCredit)}
          </div>
        </div>

        <div
          style={{
            background: 'var(--bg3)',
            borderRadius: 12,
            padding: '14px',
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: 'var(--text2)',
              marginBottom: 4,
            }}
          >
            DÉBITS
          </div>

          <div
            style={{
              fontFamily: 'Syne',
              fontWeight: 800,
              fontSize: 20,
              color: 'var(--red)',
            }}
          >
            -{formatPrice(totalDebit)}
          </div>
        </div>
      </div>

      {entries.length === 0 ? (
        <EmptyState
          icon="📒"
          title="Cartable vide"
          text="Aucun mouvement enregistré pour cet utilisateur."
        />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {entries.map((e) => {
            const t =
              TYPE_STYLE[e.category] ||
              TYPE_STYLE[e.direction] ||
              TYPE_STYLE[Number(e.amount) >= 0 ? 'credit' : 'debit'];

            const isCredit = t.sign === '+';

            return (
              <div
                key={e.transaction_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '12px 16px',
                  background: 'var(--bg3)',
                  borderRadius: 12,
                  borderLeft: `4px solid ${t.color}`,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    {e.description || t.label}
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      color: 'var(--text3)',
                    }}
                  >
                    {formatDate(e.created_at)}

                    {e.order_id && (
                      <span
                        style={{
                          marginLeft: 10,
                          fontFamily: 'monospace',
                        }}
                      >
                        Commande #{e.order_id}
                      </span>
                    )}
                  </div>
                </div>

                <Badge className={t.cls}>
                  {t.label}
                </Badge>

                <div
                  style={{
                    minWidth: 110,
                    textAlign: 'right',
                    fontFamily: 'Syne',
                    fontWeight: 800,
                    fontSize: 16,
                    color: t.color,
                  }}
                >
                  {isCredit ? '+' : '-'}
                  {formatPrice(Math.abs(Number(e.amount)))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const CartableModal = ({ userId, userName, open, onClose }) => (
  <Modal
    open={open}
    onClose={onClose}
    title={`📒 Cartable — ${userName}`}
    size="md"
    footer={
      <button
        className="btn btn-secondary"
        onClick={onClose}
      >
        Fermer
      </button>
    }
  >
    {open && <CartableContent userId={userId} />}
  </Modal>
);